import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import LoadingIcon from './LoadingIcon'
import UpdatePolicyExternalLinkModal from './UpdatePolicyExternalLinkModal'
import UpdatePaymentMethodChoiceModal from './UpdatePaymentMethodChoiceModal'
import { CalendarIcon, ChevronLeftIcon, PhoneIcon } from './ui'
import { useApi } from '../services/api'
import type { PolicyLink } from '../services/types'
import {
  getHeaderVehicleLine,
  instalmentRowFromPolicy,
  paymentTabTermPremiumLabel,
} from '../utils/policyDisplay'
import PolicyReferralBlocked from './PolicyReferralBlocked'
import PolicyTabNav from './PolicyTabNav'
import {
  getPolicyReferralMessageFromApiError,
  isPolicyPortalBlockedApiError,
  isPolicyUpdatesBlockedSummary,
  isReferralBlockedPolicySummary,
} from '../utils/policyReferralState'
import { policyIsUnsupportedVehicleType } from '../utils/policyType'

/** Calendar date-only (YYYY-MM-DD) parsed in local time — avoids UTC midnight shifting en-AU display. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

function parseToLocalDate(value: string): Date | null {
  const trimmed = value.trim()
  const m = DATE_ONLY.exec(trimmed)
  if (m) {
    const year = Number(m[1])
    const month = Number(m[2])
    const day = Number(m[3])
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
    const d = new Date(year, month - 1, day)
    if (Number.isNaN(d.getTime())) return null
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null
    return d
  }
  const d = new Date(trimmed)
  if (Number.isNaN(d.getTime())) return null
  return d
}

function formatAuLongDate(iso: string | undefined): string | null {
  if (!iso) return null
  const d = parseToLocalDate(iso)
  if (!d) return null
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * Policy term range: either the term pair or the cover pair — never mix start from one and end from the other.
 * If any term date exists, only termStartDate/termEndDate are used (no cover fallbacks for that row).
 */
function buildTermRangeDisplay(policy: {
  termStartDate?: string
  termEndDate?: string
  coverStartDate?: string
  coverEndDate?: string
}): string {
  const useTerm = Boolean(policy.termStartDate || policy.termEndDate)
  const start = useTerm ? policy.termStartDate : policy.coverStartDate
  const end = useTerm ? policy.termEndDate : policy.coverEndDate
  const a = formatAuLongDate(start)
  const b = formatAuLongDate(end)
  if (a && b) return `${a} – ${b}`
  if (a) return `From ${a}`
  return 'N/A'
}

function formatPremiumAmount(value: number | string | undefined | null): string {
  if (value === undefined || value === null) return 'N/A'
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return 'N/A'
  return `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function extractLast4(masked: string | undefined): string | null {
  if (!masked) return null
  const digits = masked.replace(/\D/g, '')
  if (digits.length < 4) return null
  return digits.slice(-4)
}

function ChevronDownIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function EditIcon() {
  return (
    <img
      src="/pencil_icon.svg"
      alt="Edit"
      className="w-5 h-5"
    />
  )
}

interface DataItemProps {
  name: string
  value: string
  /** Smaller secondary line directly under the main value (e.g. card last 4 under payment method). */
  valueDetail?: string
  editType?: 'external' | 'pencil' | null
  onEdit?: () => void
}

function DataItem({ name, value, valueDetail, editType = null, onEdit }: DataItemProps) {
  const actionLabel = editType === 'external' ? `Call us to update ${name}` : 'Edit'
  const actionText = editType === 'external' ? 'Update' : 'Edit'

  return (
    <div className="border-t border-border-primary flex gap-[12px] md:gap-[12px] items-center px-[6px] py-[16px] relative w-full">
      <div className="flex flex-col gap-[6px] items-start flex-1">
        <p className="font-body font-regular text-md md:text-lg leading-md md:leading-lg text-text-tertiary-600 w-full">
          {name}
        </p>
        <div className="flex flex-col gap-[2px] items-start w-full">
          <p className="font-body font-semibold text-md md:text-lg leading-md md:leading-lg text-text-primary-900 w-full">
            {value}
          </p>
          {valueDetail ? (
            <p className="font-body font-regular text-sm leading-sm md:text-md md:leading-md text-text-tertiary-600 w-full">
              {valueDetail}
            </p>
          ) : null}
        </div>
      </div>
      {editType && (
        <button
          type="button"
          onClick={onEdit}
          aria-label={actionLabel}
          className="flex gap-[6px] items-center justify-center overflow-clip relative shrink-0"
        >
          <div
            className={`overflow-clip relative shrink-0 size-5 ${
              editType === 'pencil' ? '' : 'text-foreground-brand-secondary-alt'
            }`}
          >
            {editType === 'external' ? (
              <PhoneIcon className="w-5 h-5" />
            ) : (
              <EditIcon />
            )}
          </div>
          <p className="font-body font-semibold text-md leading-md text-text-brand-secondary-700">
            {actionText}
          </p>
        </button>
      )}
    </div>
  )
}

interface CollapsibleSectionProps {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  isOpen?: boolean
  onToggle?: () => void
}

function CollapsibleSection({ icon, title, children, isOpen = false, onToggle }: CollapsibleSectionProps) {
  const sectionID = `section-${title.toLowerCase().replace(/\s+/g, '-')}`
  return (
    <div className="bg-background-primary border border-border-secondary border-solid flex flex-col gap-[12px] items-start px-[16px] py-[12px] relative rounded-lg shadow-xs w-full">
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={sectionID}
        className="flex gap-[12px] items-center relative shrink-0 w-full text-left"
      >
        <div className="bg-background-tertiary overflow-clip relative rounded-full shrink-0 size-12">
          <div className="absolute left-3 top-3 overflow-clip size-6 text-text-tertiary-600">
            {icon}
          </div>
        </div>
        <p className="flex-1 font-display font-medium text-display-xs leading-[32px] text-text-primary-900 min-w-0 overflow-ellipsis overflow-hidden whitespace-nowrap">
          {title}
        </p>
        <div className={`overflow-clip relative shrink-0 size-4 text-text-tertiary-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDownIcon />
        </div>
      </button>
      {isOpen && (
        <div id={sectionID} className="flex flex-col items-start relative shrink-0 w-full">
          {children}
        </div>
      )}
    </div>
  )
}

export default function PaymentView() {
  const navigate = useNavigate()
  const location = useLocation()
  const { policyId } = useParams()
  const { getPolicy, getPolicies } = useApi()
  const [loading, setLoading] = useState(true)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ payment: true, schedule: true })
  const [showExternalLinkModal, setShowExternalLinkModal] = useState(false)
  const [showUpdatePaymentMethodChoice, setShowUpdatePaymentMethodChoice] = useState(false)
  const [editingField, setEditingField] = useState('')
  const [policyData, setPolicyData] = useState<{
    regoNumber: string
    /** Same short year/make/model line as Policy tab header */
    headerVehicleLine: string
    has4X4Vehicle?: boolean
    non4x4InsuredAssetLabel?: string
    renewalDate: string
    policyNumber: string
    termPremiumDisplay: string
    policyTermDisplay: string
    paymentMethodDisplay: string
    paymentCardLast4?: string
    paymentAccountLast4?: string
    paymentVaultKeyExists?: string
    paymentFrequency: string
    autoRenewal: string
    remainingInstalments: string
    /** Shown above term premium when Entsia supplies billing-plan instalment + frequency. */
    instalmentRow: { name: string; value: string } | null
  } | null>(null)
  const [policyForClaimsNav, setPolicyForClaimsNav] = useState<PolicyLink | null>(null)
  const [referralMessage, setReferralMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!location.state?.preserveScroll) {
      window.scrollTo(0, 0)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const fetchPolicyData = async () => {
      if (!policyId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setReferralMessage(null)
        const decodedPolicyRef = policyId ? decodeURIComponent(policyId) : ''

        let policy: PolicyLink
        try {
          policy = await getPolicy(decodedPolicyRef)
        } catch (error: any) {
          if (isPolicyPortalBlockedApiError(error)) {
            if (!cancelled) setReferralMessage(getPolicyReferralMessageFromApiError(error))
            return
          }
          if (error?.status === 404 || error?.message?.includes('404') || error?.message?.includes('HTML')) {
            const response = await getPolicies()
            const foundPolicy = response.policies.find((p) => p.policyRef === decodedPolicyRef)
            if (!foundPolicy) throw new Error('Policy not found')
            if (isReferralBlockedPolicySummary(foundPolicy)) {
              if (!cancelled) {
                setReferralMessage(foundPolicy.referralMessage ?? null)
              }
              return
            }
            policy = foundPolicy
          } else {
            throw error
          }
        }

        if (cancelled) return

        if (isReferralBlockedPolicySummary(policy)) {
          if (!cancelled) {
            setReferralMessage(policy.referralMessage ?? null)
            setPolicyForClaimsNav(null)
          }
          return
        }

        const policyNumber = policy.policyNumber || policy.policyRef
        const regoNumber = policy.registrationNumber || policyNumber
        const headerVehicleLine = getHeaderVehicleLine(policy)

        let renewalDate: string
        if (policy.renewalDateFormatted) {
          renewalDate = policy.renewalDateFormatted
        } else if (policy.renewalDate) {
          const rd = parseToLocalDate(String(policy.renewalDate))
          renewalDate = rd
            ? rd.toLocaleDateString('en-AU', {
                day: 'numeric', month: 'long', year: 'numeric'
              })
            : new Date(policy.renewalDate).toLocaleDateString('en-AU', {
                day: 'numeric', month: 'long', year: 'numeric'
              })
        } else {
          const linkedDate = new Date(policy.linkedAt)
          linkedDate.setFullYear(linkedDate.getFullYear() + 1)
          renewalDate = linkedDate.toLocaleDateString('en-AU', {
            day: 'numeric', month: 'long', year: 'numeric'
          })
        }

        const termPremiumDisplay = formatPremiumAmount(policy.premium)
        const policyTermDisplay = buildTermRangeDisplay(policy)
        const paymentMethodDisplay =
          policy.paymentMethodDisplay && String(policy.paymentMethodDisplay).trim() !== ''
            ? String(policy.paymentMethodDisplay)
            : 'N/A'
        const paymentVaultKeyExists =
          policy.paymentVaultKeyExists === 'YES' || policy.paymentVaultKeyExists === 'NO'
            ? policy.paymentVaultKeyExists
            : undefined
        const paymentCardLast4 = extractLast4(policy.paymentCardMasked)
        const paymentAccountLast4 = extractLast4(policy.paymentAccountMasked)

        const paymentFrequency = policy.paymentFrequency
          ? policy.paymentFrequency.charAt(0) + policy.paymentFrequency.slice(1).toLowerCase()
          : 'N/A'

        const autoRenewal = policy.autoRenewal || 'N/A'

        let remainingInstalments = ''
        if (policy.remainingInstalments !== null && policy.remainingInstalments !== undefined) {
          const count = policy.remainingInstalments
          const freqLabel = policy.paymentFrequency === 'MONTHLY' ? 'monthly'
            : policy.paymentFrequency === 'QUARTERLY' ? 'quarterly'
            : 'annual'
          remainingInstalments = `${count} ${freqLabel} payment${count !== 1 ? 's' : ''}`
        }

        const instalmentRow = instalmentRowFromPolicy(policy)

        if (!cancelled) {
          setPolicyData({
            regoNumber,
            headerVehicleLine,
            has4X4Vehicle: policy.has4X4Vehicle,
            non4x4InsuredAssetLabel: policy.non4x4InsuredAssetLabel,
            renewalDate,
            policyNumber,
            termPremiumDisplay,
            policyTermDisplay,
            paymentMethodDisplay,
            paymentCardLast4: paymentCardLast4 ?? undefined,
            paymentAccountLast4: paymentAccountLast4 ?? undefined,
            paymentVaultKeyExists,
            paymentFrequency,
            autoRenewal,
            remainingInstalments,
            instalmentRow,
          })
          setPolicyForClaimsNav(policy)
          setLoading(false)
        }
      } catch (error) {
        if (isPolicyPortalBlockedApiError(error)) {
          if (!cancelled) setReferralMessage(getPolicyReferralMessageFromApiError(error))
          return
        }
        console.error('Error fetching policy data:', error)
        if (!cancelled) {
          navigate('/dashboard', { replace: true })
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchPolicyData()
    return () => { cancelled = true }
  }, [policyId, getPolicy, getPolicies, navigate])

  const toggleSection = (sectionName: string) => {
    setOpenSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }))
  }

  const handleEdit = (field: string) => {
    setEditingField(field)
    setShowExternalLinkModal(true)
  }

  const handleUpdatePaymentMethodClick = () => {
    if (policyData?.paymentVaultKeyExists === 'YES' && policyId) {
      setShowUpdatePaymentMethodChoice(true)
      return
    }
    handleEdit('Payment method')
  }

  const isUnsupportedType = policyIsUnsupportedVehicleType(policyForClaimsNav)
  const policyUpdatesBlocked = isPolicyUpdatesBlockedSummary(policyForClaimsNav)
  const ed = (t: 'external' | 'pencil'): 'external' | 'pencil' | undefined =>
    isUnsupportedType || policyUpdatesBlocked ? undefined : t

  const navigateToChangePaymentMethod = (method: 'card' | 'direct_debit') => {
    setShowUpdatePaymentMethodChoice(false)
    if (!policyId) return
    const q = method === 'direct_debit' ? '?method=direct_debit' : '?method=card'
    navigate(`/payment/${encodeURIComponent(policyId)}/change-method${q}`)
  }

  if (referralMessage != null) {
    return (
      <PolicyReferralBlocked
        message={referralMessage}
        onBackToDashboard={() => {
          sessionStorage.removeItem('club4x4_policies_cache')
          navigate('/dashboard')
        }}
      />
    )
  }

  if (loading || !policyData) {
    return (
      <div className="bg-background-tertiary flex flex-col min-h-screen w-full">
        <DashboardNavigation />
        <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] md:py-[48px] relative shrink-0 w-full">
          <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
            <button
              onClick={() => {
                sessionStorage.removeItem('club4x4_policies_cache')
                navigate('/dashboard')
              }}
              className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
            >
              <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
                <ChevronLeftIcon />
              </div>
              <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
                Back to dashboard
              </p>
            </button>
          </div>
        </div>
        <main id="main" className="flex flex-col flex-1 min-h-0 w-full px-[16px] md:px-[32px] py-[40px] md:py-[96px] max-w-container-desktop mx-auto">
          <h1 className="sr-only">Payment</h1>
          <div className="flex flex-1 min-h-0 w-full items-center justify-center">
            <LoadingIcon size="medium" message="Loading payment..." />
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="bg-background-brand-primary flex flex-col items-start relative min-h-screen w-full">
      <DashboardNavigation />

      <main id="main" className="flex w-full flex-col items-start">
      {/* Header Section */}
      <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] relative shrink-0 w-full">
        <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
          <div className="flex flex-col gap-[24px] items-start max-w-[768px] relative shrink-0 w-full">
            <button
              onClick={() => {
                sessionStorage.removeItem('club4x4_policies_cache')
                navigate('/dashboard')
              }}
              className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
            >
              <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
                <ChevronLeftIcon />
              </div>
              <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
                Back to dashboard
              </p>
            </button>

            <div className="flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              <div className="content-start flex flex-wrap gap-[8px] items-center relative shrink-0 w-full">
                <div className="bg-background-primary border border-text-primary-900 border-solid flex items-center px-[8px] py-[4px] relative rounded-xs shrink-0">
                  <p className="font-body font-medium text-md leading-md text-text-primary-900 text-center">
                    {policyData.regoNumber}
                  </p>
                </div>
                {policyData.has4X4Vehicle === false && (
                  <span className="font-body font-medium text-sm leading-sm text-text-primary-on-brand bg-white/20 px-[8px] py-[4px] rounded-xs">
                    {policyData.non4x4InsuredAssetLabel?.trim() || 'Non-4x4 policy'}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                <div className="flex gap-[8px] items-center relative shrink-0 w-full">
                  <h1 className="m-0 flex-1 min-w-0 relative shrink-0 font-display text-display-sm font-regular leading-[38px] text-text-primary-on-brand">
                    {policyData.headerVehicleLine}
                  </h1>
                </div>
                <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm md:leading-md relative shrink-0 text-text-primary-on-brand text-sm md:text-md w-full">
                  <p className="font-body font-regular relative shrink-0">Renews on{' '}</p>
                  <p className="font-body font-medium relative shrink-0">{policyData.renewalDate}</p>
                </div>
                <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm md:leading-md relative shrink-0 text-text-primary-on-brand text-sm md:text-md w-full">
                  <p className="font-body font-regular relative shrink-0">Policy Number{' '}</p>
                  <p className="font-body font-semibold relative shrink-0">{policyData.policyNumber}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row gap-[32px] items-start max-w-container-desktop px-[16px] md:px-[32px] py-[32px] md:py-[48px] relative shrink-0 w-full mx-auto">
        <PolicyTabNav policyId={policyId} activeTab="payment" policyForClaimsNav={policyForClaimsNav} />

        {/* Payment Content */}
        <div className="flex flex-1 flex-col gap-[32px] items-center max-w-[768px] min-w-0 relative shrink-0 w-full">
          {/* Update Payment Method Button — available for all policy types */}
          <button
            type="button"
            onClick={handleUpdatePaymentMethodClick}
            className="bg-background-brand-solid flex gap-[6px] items-center justify-center overflow-clip px-[18px] py-[12px] relative rounded-md shadow-xs shrink-0 w-full"
          >
            <div className="overflow-clip relative shrink-0 size-5 text-foreground-brand-secondary-alt">
              <EditIcon />
            </div>
            <span className="font-body font-semibold text-md text-text-white">
              Update payment method
            </span>
          </button>

          {/* Payment Section */}
          <CollapsibleSection
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
              </svg>
            }
            title="Payment"
            isOpen={openSections.payment}
            onToggle={() => toggleSection('payment')}
          >
            {policyData.instalmentRow && (
              <DataItem name={policyData.instalmentRow.name} value={policyData.instalmentRow.value} />
            )}
            <DataItem
              name={paymentTabTermPremiumLabel(policyData.paymentFrequency)}
              value={policyData.termPremiumDisplay}
            />
            <DataItem name="Term Date" value={policyData.policyTermDisplay} />
            <DataItem
              name="Payment method"
              value={policyData.paymentMethodDisplay}
              valueDetail={
                policyData.paymentCardLast4
                  ? `Card ending in ${policyData.paymentCardLast4}`
                  : policyData.paymentAccountLast4
                  ? `Account ending in ${policyData.paymentAccountLast4}`
                  : undefined
              }
              editType="pencil"
              onEdit={handleUpdatePaymentMethodClick}
            />
          </CollapsibleSection>

          {/* Schedule Section */}
          <CollapsibleSection
            icon={<CalendarIcon className="w-6 h-6" />}
            title="Schedule"
            isOpen={openSections.schedule}
            onToggle={() => toggleSection('schedule')}
          >
            <DataItem name="Renewal date" value={policyData.renewalDate} />
            <DataItem name="Payment frequency" value={policyData.paymentFrequency} editType={ed('external')} onEdit={() => handleEdit('Payment frequency')} />
            <DataItem name="Auto renewal" value={policyData.autoRenewal} editType={ed('external')} onEdit={() => handleEdit('Auto renewal')} />
            {policyData.autoRenewal !== 'On' && policyData.remainingInstalments && (
              <DataItem name="Remaining instalments" value={policyData.remainingInstalments} />
            )}
          </CollapsibleSection>
        </div>
      </div>
      </main>

      <Footer />

      {showExternalLinkModal && (
        <UpdatePolicyExternalLinkModal
          fieldName={editingField}
          onClose={() => setShowExternalLinkModal(false)}
        />
      )}

      {showUpdatePaymentMethodChoice && (
        <UpdatePaymentMethodChoiceModal
          onClose={() => setShowUpdatePaymentMethodChoice(false)}
          onSelectCard={() => navigateToChangePaymentMethod('card')}
          onSelectDirectDebit={() => navigateToChangePaymentMethod('direct_debit')}
        />
      )}
    </div>
  )
}
